import { FC } from 'react'
import { ResolvedFunction } from '../../types/functions'
import { FunctionHeader } from './FunctionHeader'
import { FunctionSignature } from './FunctionSignature'
import { FunctionParameters } from './FunctionParameters'
import { FunctionReturns } from './FunctionReturns'
import { FunctionCodePanel } from './FunctionCodePanel'
import { ChevronLeft, ChevronRight } from 'lucide-react'

interface FunctionDetailProps {
  func: ResolvedFunction
  prevFunc?: ResolvedFunction | null
  nextFunc?: ResolvedFunction | null
  onSelectFunction?: (fn: ResolvedFunction) => void
  onSelectCategory?: (categoryName: string, moduleName: string) => void
}

export const FunctionDetail: FC<FunctionDetailProps> = ({
  func,
  prevFunc,
  nextFunc,
  onSelectFunction,
  onSelectCategory
}) => {
  return (
    <article id={`fn-${func.anchorId}`} className="ns-function-card">
      <div className="ns-function-grid">
        {/* Left Column: Documentation & Parameter Schema */}
        <div className="ns-function-left-col">
          <FunctionHeader func={func} onSelectCategory={onSelectCategory} />
          <FunctionSignature signature={func.signatureString} />
          <FunctionParameters parameters={func.parameters} />
          <FunctionReturns returns={func.returns} />

          {/* Page-by-Page Sequential Navigation */}
          {onSelectFunction && (prevFunc || nextFunc) && (
            <nav className="ns-function-pagination" aria-label="Function pagination">
              {prevFunc ? (
                <button
                  type="button"
                  className="ns-pagination-card ns-pagination-prev"
                  onClick={() => onSelectFunction(prevFunc)}
                >
                  <div className="ns-pagination-label">
                    <ChevronLeft size={16} />
                    <span>Previous</span>
                  </div>
                  <div className="ns-pagination-title">{prevFunc.displayName}</div>
                  <div className="ns-pagination-meta">{prevFunc.module} · {prevFunc.category}</div>
                </button>
              ) : (
                <div className="ns-pagination-spacer" />
              )}

              {nextFunc ? (
                <button
                  type="button"
                  className="ns-pagination-card ns-pagination-next"
                  onClick={() => onSelectFunction(nextFunc)}
                >
                  <div className="ns-pagination-label">
                    <span>Next</span>
                    <ChevronRight size={16} />
                  </div>
                  <div className="ns-pagination-title">{nextFunc.displayName}</div>
                  <div className="ns-pagination-meta">{nextFunc.module} · {nextFunc.category}</div>
                </button>
              ) : (
                <div className="ns-pagination-spacer" />
              )}
            </nav>
          )}
        </div>

        {/* Right Column: Interactive Code & Execution Payload Panel */}
        <div className="ns-function-right-col">
          <div className="ns-sticky-code-container">
            <FunctionCodePanel func={func} />
          </div>
        </div>
      </div>
    </article>
  )
}
